<script lang="ts" setup>
import type { UseQueryReturnType } from '@tanstack/vue-query'

import type { Response } from '~/types/api/v1'
import type { TemplateData } from '~/composables/api/template/useFetchTemplate'
import type { UserTemplateData } from '~/composables/api/template/user-template/useFetchUserTemplate'

const props = defineProps<{
  query: UseQueryReturnType<Response<TemplateData | UserTemplateData>, Error>
}>()

const { t } = useI18n()
const breakpoints = useBreakpointHelper()
const isMobile = breakpoints.smaller('xl')
const isDesktop = breakpoints.greaterOrEqual('xl')
const template = computed(() => props.query.data.value?.data)
const isFetchingDelayed = refDelayed(props.query.isFetching, false)
const onContractCreated = (contractId: string) => navigateTo(`/builder/edit?cId=${contractId}`, { external: true })
const isCreateDocumentModalOpen = ref(false)
</script>

<template>
  <TemplateDetailLayout>
    <template #sidebar>
      <div
        v-if="isDesktop && template"
        class="flex w-full flex-col gap-10 xl:w-[372px]"
      >
        <div
          class="
              flex
              w-full
              flex-col
              gap-2
              rounded-lg
              border
              border-gray-200
              p-6
              text-sm
              shadow-lg
              "
        >
          <slot name="meta" />
          <DTButton
            variant="blue"
            class="w-full"
            @click="isCreateDocumentModalOpen = true"
          >
            {{ t('template.create-document') }}
          </DTButton>
        </div>

        <div class="px-6">
          <slot name="aside" />
        </div>
      </div>
    </template>

    <DTLoaderDots v-if="isFetchingDelayed && !template" />
    <article
      v-if="template"
      class="mb-20 flex flex-col gap-6 xl:mb-0"
    >
      <div>
        <TemplateTypeLabel
          :template="template"
          class="mb-2 text-xs"
        />
        <h1 class="text-3xl font-semibold">
          {{ template?.attributes.name }}
        </h1>
      </div>

      <div class="flex flex-wrap items-center gap-2.5 xl:mb-10">
        <TemplateFavouriteButton :template />
        <slot name="actions" />
      </div>

      <div
        v-if="isMobile"
        class="my-4 flex max-w-xl flex-col gap-2 border-y border-gray-200 py-4"
      >
        <slot name="meta" />
        <DTButton
          variant="blue"
          class="w-full"
          @click="isCreateDocumentModalOpen = true"
        >
          {{ t('template.create-document') }}
        </DTButton>
      </div>

      <p class="max-w-xl text-xl font-normal xl:max-w-max">
        {{ template?.attributes.shortDescription }}
      </p>

      <slot name="media" />

      <UiProse>
        <div v-html="template?.attributes.descriptionHtml" />
      </UiProse>

      <aside v-if="isMobile">
        <slot name="aside" />
      </aside>
    </article>
    <LazyTemplateCreateDocumentModal
      v-if="template"
      v-model="isCreateDocumentModalOpen"
      :template
      @success="onContractCreated"
    />
  </TemplateDetailLayout>
</template>
